/**
 * Окно «Добавить референсы» страницы «Генерация» (06.10.26): выбор по картинкам, не уходя со страницы.
 *
 * Две вкладки — пины подборки (плитка `PinGrid`, как в `/trends/:id/refs`) и референсы библиотек
 * (корзина, все или одна библиотека). Пины хранятся по подборкам — общего списка нет, подборка
 * выбирается в окне, последняя помнится в браузере. Фильтры — на клиенте, по одному ответу; в адрес
 * страницы они не пишутся: адрес — набор генерации.
 *
 * Клик по плитке отмечает; отметки обеих вкладок живут вместе, «Добавить N» отдаёт их странице.
 * Уже стоящие в генерации — отмечены и не снимаются. Потолок кадров окно не держит — его
 * показывает страница. Сетка — порциями по `PAGE`: подборка бывает в тысячи пинов.
 *
 * `libraryId` — окно только референсов, открытое на этой библиотеке (референсы композера сценария
 * ролика, 06.10.26): вкладки пинов нет, источник можно сменить.
 *
 * `dresses` — платья страницы с папкой (07.10.26): на вкладке библиотек появляется «Поиск по фото» —
 * выдача по сходству с выбранным платьем в текущем источнике заменяет список (плитки с процентом),
 * «сбросить» возвращает список источника; отметки при поиске и сбросе не трогаются. Нет пропа — нет кнопки.
 * Смена источника при показанном поиске перезапускает его в новой области (№434, 29.10.26).
 *
 * `single` — один выбор, радио (каталог, №435): клик заменяет отметку, уже стоящий референс не заблокирован,
 * а подписан «сейчас в паре»; кнопка — «Выбрать», пока отмечено то же, что стоит, — неактивна.
 */
import { useQuery } from "@tanstack/react-query";
import { Check, ImageOff, Loader2, ScanSearch } from "lucide-react";
import { useMemo, useState } from "react";

import { Dialog } from "@/components/trends/Dialog";
import { PinGrid, RetryImg } from "@/components/trends/PinGrid";
import { sortPins, type SortKey } from "@/components/trends/References";
import { btn, btnGhost, btnPrimary, Empty, ErrorLine, input, Loading, Segmented } from "@/components/trends/ui";
import {
  flatLibraries,
  libraryKeys,
  loadAllItems,
  loadBasket,
  loadLibraries,
  plural,
  searchSimilar,
  verdictLabel,
  type ReferenceItem,
  type Verdict,
} from "@/lib/library";
import { num, thumbUrl } from "@/lib/media";
import {
  pickOne,
  rescope,
  scoreLabel,
  scoreMap,
  searchScope,
  similarKey,
  similarView,
  viewCaption,
  type SentSearch,
  type SimilarView,
} from "@/lib/similar";
import { loadTags, tagKeys, type Tag } from "@/lib/tags";
import { loadTopicPins, loadTopics, retryQuery, trendKeys, type Pin } from "@/lib/trends";

/** Плиток в порции сетки. */
const PAGE = 120;
const TOPIC_KEY = "reels.generate.topic";

type Tab = "pins" | "refs";
type KindFilter = "all" | "image" | "video";
type LibraryFilter = "all" | "in" | "out";
type VerdictFilter = Verdict | "none" | "all";
/** Источник референсов: корзина, все библиотеки или id библиотеки строкой. */
type Source = "basket" | "all" | `${number}`;

const NO_PINS: Pin[] = [];
const NO_ITEMS: ReferenceItem[] = [];
const NO_TAGS: Tag[] = [];
const NO_BACKS: ReadonlySet<number> = new Set();
const NO_SCORES: ReadonlyMap<number, number> = new Map();

/** Платье страницы для поиска по фото: `id` папки «Наших фото» и заголовок. */
export type PageDress = {
  id: number;
  title: string;
  /** Отмеченные фото платья на странице (пусто — искать по всем). */
  photoIds: number[];
  /** Миниатюра в шапке окна (№429): обложка или первое отмеченное; null — не показываем. */
  thumbPath: string | null;
};

function storedTopic(): number | null {
  try {
    const n = Number(window.localStorage.getItem(TOPIC_KEY));
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function storeTopic(id: number) {
  try {
    window.localStorage.setItem(TOPIC_KEY, String(id));
  } catch {
    // закрытое хранилище — выбор живёт до закрытия окна
  }
}

/** `select` тега: значение — id строкой, «none» или пусто (все). */
function tagValue(v: number | "none" | null): string {
  return v === null ? "" : String(v);
}
function parseTag(v: string): number | "none" | null {
  return v === "" ? null : v === "none" ? "none" : Number(v);
}

export function RefPickerDialog({
  open,
  onClose,
  takenPins,
  takenRefs,
  onAdd,
  libraryId,
  dresses,
  refsOnly,
  single,
  title = "Добавить референсы",
}: {
  open: boolean;
  onClose: () => void;
  /** Пины и референсы, что уже в генерации. */
  takenPins: readonly number[];
  takenRefs: readonly number[];
  onAdd: (pins: number[], refs: number[]) => void;
  /** Только референсы, начиная с этой библиотеки. */
  libraryId?: number | undefined;
  /** Платья страницы с папкой: есть — на вкладке библиотек «Поиск по фото». */
  dresses?: readonly PageDress[] | undefined;
  /** Только референсы библиотек, источник — «все библиотеки» (каталог №431): вкладки пинов нет. */
  refsOnly?: boolean | undefined;
  /** Один референс, радио (каталог №435): `takenRefs` — то, что стоит сейчас. */
  single?: boolean | undefined;
  title?: string;
}) {
  const thumbs = (dresses ?? []).filter((d) => d.thumbPath !== null);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        thumbs.length > 0 ? (
          <span className="flex items-center gap-3">
            {title}
            <DressThumbs dresses={thumbs} />
          </span>
        ) : (
          title
        )
      }
      wide
    >
      {/* Содержимое монтируется заново на каждом открытии — отметки прошлого раза не живут. */}
      <Picker takenPins={takenPins} takenRefs={takenRefs} onAdd={onAdd} onClose={onClose} libraryId={libraryId} dresses={dresses} refsOnly={refsOnly} single={single} />
    </Dialog>
  );
}

function Picker({
  takenPins,
  takenRefs,
  onAdd,
  onClose,
  libraryId,
  dresses,
  refsOnly,
  single,
}: {
  takenPins: readonly number[];
  takenRefs: readonly number[];
  onAdd: (pins: number[], refs: number[]) => void;
  onClose: () => void;
  libraryId: number | undefined;
  dresses: readonly PageDress[] | undefined;
  refsOnly: boolean | undefined;
  single: boolean | undefined;
}) {
  const noPins = libraryId !== undefined || refsOnly === true;
  const [tab, setTab] = useState<Tab>(noPins ? "refs" : "pins");
  const [pins, setPins] = useState<ReadonlySet<number>>(() => new Set());
  const [refs, setRefs] = useState<ReadonlySet<number>>(() => new Set());
  const tagsQuery = useQuery({ queryKey: tagKeys.list, queryFn: loadTags });
  const tags = tagsQuery.data?.tags ?? NO_TAGS;
  const tagNames = useMemo(() => new Map(tags.map((t) => [t.id, t.name])), [tags]);

  const flip = (set: ReadonlySet<number>, id: number) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };
  const total = pins.size + refs.size;

  return (
    <>
      {!noPins ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
          <Segmented<Tab>
            label="откуда"
            value={tab}
            onPick={setTab}
            options={[
              { value: "pins", label: `пины подборки${pins.size ? ` · ${pins.size}` : ""}` },
              { value: "refs", label: `библиотеки${refs.size ? ` · ${refs.size}` : ""}` },
            ]}
          />
        </div>
      ) : null}
      <div className="flex-1">
        {tab === "pins" ? (
          <PinsTab
            taken={takenPins}
            picked={pins}
            onToggle={(id) => setPins((s) => flip(s, id))}
            tags={tags}
            tagNames={tagNames}
          />
        ) : (
          <RefsTab
            taken={takenRefs}
            picked={refs}
            onToggle={(id) => setRefs((s) => (single ? pickOne(id, takenRefs) : flip(s, id)))}
            single={single === true}
            tags={tags}
            tagNames={tagNames}
            libraryId={libraryId}
            dresses={dresses}
            allByDefault={refsOnly === true}
          />
        )}
      </div>
      <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-end gap-2 border-t border-border bg-bg px-4 py-2.5">
        <span className="mr-auto text-[12px] text-fg-faint tnum">
          {single
            ? "клик по картинке выбирает референс пары"
            : total
            ? !noPins
              ? `отмечено: пинов ${pins.size}, референсов ${refs.size}`
              : `отмечено референсов: ${refs.size}`
            : "клик по картинке отмечает; уже добавленные отмечены"}
        </span>
        <button type="button" className={btn} onClick={onClose}>
          Отмена
        </button>
        <button
          type="button"
          className={btnPrimary}
          disabled={total === 0}
          onClick={() => {
            onAdd([...pins], [...refs]);
            onClose();
          }}
        >
          {single ? "Выбрать" : `Добавить ${total || ""}`}
        </button>
      </div>
    </>
  );
}

function PinsTab({
  taken,
  picked,
  onToggle,
  tags,
  tagNames,
}: {
  taken: readonly number[];
  picked: ReadonlySet<number>;
  onToggle: (id: number) => void;
  tags: Tag[];
  tagNames: ReadonlyMap<number, string>;
}) {
  const topics = useQuery({ queryKey: trendKeys.list, queryFn: loadTopics, retry: retryQuery });
  const withRefs = useMemo(() => (topics.data?.topics ?? []).filter((t) => t.refs > 0), [topics.data]);
  const [chosen, setChosen] = useState<number | null>(storedTopic);
  // Запомненной подборки нет (удалили, пинов не стало) — первая с пинами.
  const topicId = withRefs.some((t) => t.id === chosen) ? chosen : (withRefs[0]?.id ?? null);
  const data = useQuery({
    queryKey: trendKeys.topicPins(topicId ?? 0),
    queryFn: () => loadTopicPins(topicId ?? 0),
    enabled: topicId !== null,
    retry: retryQuery,
  });
  const all = data.data?.pins ?? NO_PINS;

  const [kind, setKind] = useState<KindFilter>("all");
  const [word, setWord] = useState<number | null>(null);
  const [lib, setLib] = useState<LibraryFilter>("all");
  const [tag, setTag] = useState<number | "none" | null>(null);
  const [sort, setSort] = useState<SortKey>("trend");
  const [limit, setLimit] = useState(PAGE);
  const reset = () => setLimit(PAGE);

  const words = useMemo(() => {
    const has = new Set(all.flatMap((p) => p.type_ids ?? []));
    return (data.data?.types ?? []).filter((w) => has.has(w.id));
  }, [all, data.data]);
  const wordNames = useMemo(() => new Map(words.map((w) => [w.id, w.name])), [words]);
  const shown = useMemo(() => {
    const out = all.filter(
      (p) =>
        (kind === "all" || p.kind === kind) &&
        (word === null || (p.type_ids ?? []).includes(word)) &&
        (lib === "all" || (p.in_libraries > 0) === (lib === "in")) &&
        (tag === null || (tag === "none" ? p.tag_ids.length === 0 : p.tag_ids.includes(tag))),
    );
    return sortPins(out, sort);
  }, [all, kind, word, lib, tag, sort]);
  const drawn = useMemo(() => shown.slice(0, limit), [shown, limit]);
  const usedTags = useMemo(() => {
    const has = new Set(all.flatMap((p) => p.tag_ids));
    return tags.filter((t) => has.has(t.id));
  }, [all, tags]);
  const selected = useMemo(() => new Set([...taken, ...picked]), [taken, picked]);

  if (topics.isPending) return <Loading />;
  if (topics.isError) return <ErrorLine error={topics.error} />;
  if (topicId === null) return <Empty>В подборках ещё нет пинов — соберите их в «Трендах».</Empty>;

  return (
    <div className="grid gap-2" data-annot="пины в окне выбора">
      <div className="grid gap-2 border-b border-border px-4 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={`${input} max-w-[320px]`}
            value={topicId}
            aria-label="подборка"
            onChange={(e) => {
              const id = Number(e.target.value);
              setChosen(id);
              storeTopic(id);
              setWord(null);
              reset();
            }}
          >
            {withRefs.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} · {num(t.refs)}
              </option>
            ))}
          </select>
          {words.length > 1 ? (
            <select
              className={`${input} max-w-[240px]`}
              value={word ?? ""}
              aria-label="слово"
              onChange={(e) => {
                setWord(e.target.value === "" ? null : Number(e.target.value));
                reset();
              }}
            >
              <option value="">все слова</option>
              {words.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          ) : null}
          <select
            className={`${input} max-w-[200px]`}
            value={tagValue(tag)}
            aria-label="тег"
            onChange={(e) => {
              setTag(parseTag(e.target.value));
              reset();
            }}
          >
            <option value="">все теги</option>
            <option value="none">без тега</option>
            {usedTags.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented<KindFilter>
            label="вид"
            value={kind}
            onPick={(v) => {
              setKind(v);
              reset();
            }}
            options={[
              { value: "all", label: "все" },
              { value: "image", label: "картинки" },
              { value: "video", label: "видео" },
            ]}
          />
          <Segmented<LibraryFilter>
            label="библиотека"
            value={lib}
            onPick={(v) => {
              setLib(v);
              reset();
            }}
            options={[
              { value: "all", label: "все" },
              { value: "in", label: "в библиотеке" },
              { value: "out", label: "не в библиотеке" },
            ]}
          />
          <Segmented<SortKey>
            label="порядок"
            value={sort}
            onPick={(v) => {
              setSort(v);
              reset();
            }}
            options={[
              { value: "trend", label: "тренд" },
              { value: "saves", label: "сохранения" },
              { value: "fresh", label: "новые" },
            ]}
          />
          <span className="text-[12px] text-fg-faint tnum">
            {num(shown.length)} из {num(all.length)}
            {data.data?.truncated ? ` · в подборке ${num(data.data.total)}, здесь топ по сохранениям` : ""}
          </span>
        </div>
      </div>
      {data.isPending ? (
        <Loading />
      ) : data.isError ? (
        <ErrorLine error={data.error} />
      ) : shown.length === 0 ? (
        <Empty>Под фильтр не попал ни один пин.</Empty>
      ) : (
        <div className="px-4 pb-3">
          <PinGrid
            pins={drawn}
            layout="masonry"
            onOpen={() => undefined}
            selected={selected}
            selecting
            onToggle={(index) => {
              const pin = drawn[index];
              if (pin && !taken.includes(pin.id)) onToggle(pin.id);
            }}
            tagNames={tagNames}
            wordNames={wordNames}
            medianOf="подборки"
          />
          {drawn.length < shown.length ? (
            <More shown={drawn.length} total={shown.length} onMore={() => setLimit((l) => l + PAGE)} />
          ) : null}
        </div>
      )}
    </div>
  );
}

function RefsTab({
  taken,
  picked,
  onToggle,
  single,
  tags,
  tagNames,
  libraryId,
  dresses,
  allByDefault,
}: {
  taken: readonly number[];
  picked: ReadonlySet<number>;
  onToggle: (id: number) => void;
  single: boolean;
  tags: Tag[];
  tagNames: ReadonlyMap<number, string>;
  libraryId?: number | undefined;
  dresses?: readonly PageDress[] | undefined;
  allByDefault?: boolean | undefined;
}) {
  const libraries = useQuery({ queryKey: libraryKeys.list, queryFn: loadLibraries });
  const basket = useQuery({ queryKey: libraryKeys.basket, queryFn: loadBasket });
  const [chosen, setChosen] = useState<Source | null>(libraryId !== undefined ? `${libraryId}` : allByDefault ? "all" : null);
  // По умолчанию — корзина, если в ней что-то есть: это и есть отобранное под генерацию.
  const source: Source = chosen ?? (basket.data?.items.length ? "basket" : "all");
  const params = source === "basket" || source === "all" ? {} : { library_id: Number(source) };
  const all = useQuery({
    queryKey: libraryKeys.all(params),
    queryFn: () => loadAllItems(params),
    enabled: source !== "basket",
    retry: retryQuery,
  });
  const q = source === "basket" ? basket : all;

  // «Поиск по фото»: платье выбирают, поиск запускает кнопка; выдача прежняя до следующего нажатия.
  const [dressPick, setDressPick] = useState<number | null>(null);
  const [sent, setSent] = useState<SentSearch | null>(null);
  const dress = dresses?.find((d) => d.id === dressPick) ?? dresses?.[0] ?? null;
  const scope = searchScope(source);
  const similar = useQuery({
    queryKey: libraryKeys.similar(sent ? similarKey(sent.dressId, sent.photoIds) : "", sent?.scope ?? null),
    queryFn: async (): Promise<SimilarView> => {
      if (!sent) throw new Error("Нечего искать.");
      const got = await searchSimilar({
        dressId: sent.dressId,
        photoIds: sent.photoIds,
        ...(sent.scope !== null ? { libraryId: sent.scope } : {}),
      });
      return similarView(got);
    },
    enabled: sent !== null,
    staleTime: Infinity,
    retry: retryQuery,
  });
  const searching = sent !== null;
  const same = sent !== null && dress !== null && sent.dressId === dress.id && sent.scope === scope && similarKey(sent.dressId, sent.photoIds) === similarKey(dress.id, dress.photoIds);
  const busy = searching && similar.isFetching;
  const canSearch = dress !== null && !busy && !(same && !similar.isError);
  const search = () => {
    if (!dress || !canSearch) return;
    if (same) {
      void similar.refetch();
      return;
    }
    setSent({ dressId: dress.id, photoIds: dress.photoIds, scope });
    setLimit(PAGE);
  };
  const sentTitle = dresses?.find((d) => d.id === sent?.dressId)?.title ?? "";
  const backs = searching ? new Set((similar.data?.items ?? []).filter((h) => h.view === "back").map((h) => h.id)) : NO_BACKS;
  const scores = searching ? scoreMap(similar.data?.items ?? []) : NO_SCORES;

  // Refine держит keepPreviousData глобально: сменили источник — в data ещё выдача прежнего, а подпись уже про
  // новый; такие плитки не рисуем (из них выбрали бы референс не из той библиотеки), показываем загрузку.
  const stale = searching ? similar.isPlaceholderData : q.isPlaceholderData;
  const items: readonly ReferenceItem[] = stale
    ? NO_ITEMS
    : searching
      ? (similar.data?.items ?? NO_ITEMS)
      : (q.data?.items ?? NO_ITEMS);

  const [tag, setTag] = useState<number | "none" | null>(null);
  const [verdict, setVerdict] = useState<VerdictFilter>("all");
  const [limit, setLimit] = useState(PAGE);
  const shown = useMemo(
    () =>
      items.filter(
        (i) =>
          (verdict === "all" || (verdict === "none" ? i.verdict === null : i.verdict === verdict)) &&
          (tag === null || (tag === "none" ? i.tag_ids.length === 0 : i.tag_ids.includes(tag))),
      ),
    [items, tag, verdict],
  );
  const drawn = useMemo(() => shown.slice(0, limit), [shown, limit]);
  const usedTags = useMemo(() => {
    const has = new Set(items.flatMap((i) => i.tag_ids));
    return tags.filter((t) => has.has(t.id));
  }, [items, tags]);
  const truncated = !searching && source !== "basket" && all.data?.truncated;

  return (
    <div className="grid gap-2" data-annot="референсы в окне выбора">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        <select
          className={`${input} max-w-[320px]`}
          value={searching && source === "basket" ? "all" : source}
          aria-label="откуда референсы"
          onChange={(e) => {
            const next = e.target.value as Source;
            setChosen(next);
            setSent((s) => rescope(s, next));
            setLimit(PAGE);
          }}
        >
          <option value="basket">Корзина · {num(basket.data?.items.length ?? 0)}</option>
          <option value="all">Все библиотеки</option>
          {(libraries.data?.groups ?? []).map((g) => (
            <optgroup key={g.code} label={g.name}>
              {g.libs.map((l) => (
                <option key={l.id} value={String(l.id)}>
                  {l.name} · {num(l.photos)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <select
          className={`${input} max-w-[200px]`}
          value={tagValue(tag)}
          aria-label="тег"
          onChange={(e) => {
            setTag(parseTag(e.target.value));
            setLimit(PAGE);
          }}
        >
          <option value="">все теги</option>
          <option value="none">без тега</option>
          {usedTags.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <Segmented<VerdictFilter>
          label="вердикт"
          value={verdict}
          onPick={(v) => {
            setVerdict(v);
            setLimit(PAGE);
          }}
          options={[
            { value: "all", label: "все" },
            { value: "works", label: "работает" },
            { value: "fails", label: "не работает" },
            { value: "none", label: "не оценён" },
          ]}
        />
        {dresses !== undefined ? (
          <>
            {dresses.length > 1 ? (
              <select
                className={`${input} max-w-[240px]`}
                value={dress?.id ?? ""}
                aria-label="платье для поиска"
                onChange={(e) => setDressPick(Number(e.target.value))}
              >
                {dresses.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                  </option>
                ))}
              </select>
            ) : dress ? (
              <span className="max-w-[240px] truncate text-[12.5px] text-fg-muted" title={dress.title}>
                {dress.title}
              </span>
            ) : null}
            <button
              type="button"
              className={btn}
              onClick={search}
              disabled={!canSearch}
              title={dress === null ? "сначала добавьте платье" : undefined}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ScanSearch size={14} />}
              {busy ? "Ищу…" : "Поиск по фото"}
            </button>
          </>
        ) : null}
        <span className="text-[12px] text-fg-faint tnum">
          {num(shown.length)} из {num(items.length)}
          {truncated ? " · показаны не все — выберите библиотеку" : ""}
        </span>
      </div>
      {sent !== null ? (
        <div className="flex flex-wrap items-center gap-2 px-4 text-[12.5px]" role="status">
          <span className="text-fg-muted">
            по платью «{sentTitle}» · {sent.scope === null ? "во всех библиотеках" : `в библиотеке «${(flatLibraries(libraries.data).find((l) => l.id === sent.scope)?.name ?? `#${sent.scope}`)}»`}
          </span>
          {viewCaption(similar.data?.query_views) ? (
            <span className="text-fg-muted">· {viewCaption(similar.data?.query_views)}</span>
          ) : null}
          {busy ? <span className="text-fg-faint">ищу похожие…</span> : null}
          {similar.data && similar.data.missing > 0 ? (
            <span className="tnum">{num(similar.data.missing)} фото ещё без вектора</span>
          ) : null}
          <button
            type="button"
            className={btnGhost}
            onClick={() => {
              setSent(null);
              setLimit(PAGE);
            }}
          >
            сбросить
          </button>
        </div>
      ) : null}
      {searching && similar.isError ? (
        <div className="px-4">
          <ErrorLine error={similar.error} />
        </div>
      ) : null}
      {searching && similar.isError ? null : stale || (searching ? similar.isPending : q.isPending) ? (
        <Loading />
      ) : !searching && q.isError ? (
        <ErrorLine error={q.error} />
      ) : items.length === 0 ? (
        <Empty>{searching ? "Ни одного похожего." : source === "basket" ? "Корзина пуста." : "Референсов нет."}</Empty>
      ) : shown.length === 0 ? (
        <Empty>Под фильтр не попал ни один референс.</Empty>
      ) : (
        <div className="px-4 pb-3">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3">
            {drawn.map((item) => (
              <RefTile
                key={item.id}
                item={item}
                score={scores.get(item.id)}
                back={backs.has(item.id)}
                taken={taken.includes(item.id)}
                on={picked.has(item.id)}
                single={single}
                anyPicked={picked.size > 0}
                onToggle={() => onToggle(item.id)}
                tagNames={tagNames}
              />
            ))}
          </div>
          {drawn.length < shown.length ? (
            <More shown={drawn.length} total={shown.length} onMore={() => setLimit((l) => l + PAGE)} />
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Плитка референса в окне: только выбор — правка, корзина и удаление живут в библиотеках. */
function RefTile({
  item,
  score,
  back,
  taken,
  on,
  single,
  anyPicked,
  onToggle,
  tagNames,
}: {
  item: ReferenceItem;
  /** Сходство с платьем из «Поиска по фото»: есть — плашка с процентом. */
  score?: number | undefined;
  /** Фото снято сзади — метка «сзади» у процента. */
  back?: boolean;
  taken: boolean;
  on: boolean;
  /** Радио (№435): стоящий сейчас не заблокирован и выбран, пока не отмечен другой. */
  single: boolean;
  anyPicked: boolean;
  onToggle: () => void;
  tagNames: ReadonlyMap<number, string>;
}) {
  const checked = single && taken ? !anyPicked : taken || on;
  const locked = taken && !single;
  const ratio = item.asset_width && item.asset_height ? `${item.asset_width} / ${item.asset_height}` : "3 / 4";
  const tagsText = item.tag_ids.map((id) => tagNames.get(id) ?? `#${id}`).join(", ");
  return (
    <figure className="min-w-0">
      <button
        type="button"
        disabled={locked}
        role={single ? "radio" : undefined}
        aria-checked={single ? checked : undefined}
        aria-pressed={single ? undefined : checked}
        onClick={onToggle}
        title={locked ? "уже в генерации" : (item.note ?? undefined)}
        className={`relative block max-h-[360px] w-full overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-subtle ${
          checked ? "outline-2 outline-offset-[-2px] outline-accent" : "hover:border-border-strong"
        } ${locked ? "cursor-default opacity-60" : ""}`}
        style={{ aspectRatio: ratio }}
      >
        {item.asset_path ? (
          <RetryImg src={thumbUrl(item.asset_path)} alt={item.note ?? ""} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <span className="absolute inset-0 grid place-items-center text-fg-faint">
            <ImageOff size={18} />
          </span>
        )}
        <span
          className={`absolute top-1.5 right-1.5 grid size-5 place-items-center border ${single ? "rounded-full" : "rounded-[4px]"} ${
            checked ? "border-accent bg-accent text-bg" : "border-white/80 bg-black/45 text-white"
          }`}
        >
          {checked ? single ? <span className="size-2 rounded-full bg-bg" /> : <Check size={13} strokeWidth={3} /> : null}
        </span>
        {score !== undefined ? (
          <span className="absolute top-1.5 left-1.5 rounded-[4px] bg-black/60 px-1.5 py-0.5 text-[11px] text-white tnum">
            {scoreLabel(score)}
            {back ? " · сзади" : ""}
          </span>
        ) : null}
        {taken ? (
          <span className="absolute bottom-1.5 left-1.5 rounded-[4px] bg-black/60 px-1.5 py-0.5 text-[11px] text-white">
            {single ? "сейчас в паре" : "уже в генерации"}
          </span>
        ) : item.verdict ? (
          <span
            className={`absolute bottom-1.5 left-1.5 rounded-[4px] px-1.5 py-0.5 text-[11px] font-medium ${
              item.verdict === "works" ? "bg-ok-bg text-ok" : "bg-err-bg text-err"
            }`}
          >
            {verdictLabel[item.verdict]}
          </span>
        ) : null}
      </button>
      <figcaption className="mt-1 grid min-w-0 gap-0.5 px-0.5 text-[11.5px] text-fg-faint">
        <span className="truncate" title={item.library_name}>
          #{item.id} · {item.library_name}
          {item.generations
            ? ` · ${item.generations} ${plural(item.generations, "генерация", "генерации", "генераций")}`
            : ""}
        </span>
        {item.note ? (
          <span className="truncate text-fg-muted" title={item.note}>
            {item.note}
          </span>
        ) : null}
        {tagsText ? (
          <span className="truncate" title={tagsText}>
            {tagsText}
          </span>
        ) : null}
      </figcaption>
    </figure>
  );
}

function More({ shown, total, onMore }: { shown: number; total: number; onMore: () => void }) {
  return (
    <div className="flex items-center justify-center gap-2 pt-4 text-[12px] text-fg-faint tnum">
      показано {num(shown)} из {num(total)}
      <button type="button" className={btn} onClick={onMore}>
        Показать ещё
      </button>
    </div>
  );
}

/** Платья страницы в шапке окна (№429): видно, под что подбирается сцена; наведение — крупнее. */
function DressThumbs({ dresses }: { dresses: readonly PageDress[] }) {
  return (
    <span className="flex items-center gap-2">
      {dresses.map((d) =>
        d.thumbPath === null ? null : (
          <span key={d.id} className="group relative block" title={d.title}>
            <img
              src={thumbUrl(d.thumbPath)}
              alt={d.title}
              className="h-12 w-9 rounded border border-border object-cover"
            />
            <img
              src={thumbUrl(d.thumbPath)}
              alt=""
              aria-hidden
              className="pointer-events-none absolute left-0 top-full z-30 mt-2 hidden h-80 w-auto max-w-none rounded-[var(--radius-card)] border border-border bg-bg object-contain shadow-xl group-hover:block"
            />
          </span>
        ),
      )}
    </span>
  );
}
